import { ChevronLeftIcon, ChevronRightIcon } from '@fourthwall/icons'
import type { DPCalendar } from '@rehookify/datepicker'
import { useDatePickerContext } from '@rehookify/datepicker'
import type { FC } from 'react'
import React, { useState } from 'react'
import { IconButton } from '../../common/IconButton/IconButton'
import { Popper } from '../../common/Popper'
import * as S from './Calendar.styled'

type CalendarMonthProps = {
  calendar: DPCalendar
  showPrevMonthButton?: boolean
  showNextMonthButton?: boolean
}

export const CalendarMonth: FC<CalendarMonthProps> = ({
  calendar,
  showPrevMonthButton,
  showNextMonthButton,
}) => {
  const {
    data: { weekDays, years },
    propGetters: { dayButton, addOffset, subtractOffset, yearButton },
  } = useDatePickerContext()
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false)

  const { month, days, year } = calendar

  const filteredYears = years.filter((year) => !year.disabled)

  return (
    <S.MonthContainer>
      <S.MonthHeader>
        {showPrevMonthButton && (
          <S.MonthHeaderPrevArrowContainer>
            <IconButton
              icon={ChevronLeftIcon}
              size="small"
              aria-label="Move backward to switch to the previous month"
              {...subtractOffset({ months: 1 })}
            />
          </S.MonthHeaderPrevArrowContainer>
        )}
        <Popper
          isOpen={isMonthPickerOpen}
          referenceComponent={
            <S.MonthHeaderButton
              type="button"
              onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}
            >
              {month} {year}
            </S.MonthHeaderButton>
          }
          popperProps={{
            placement: 'bottom',
          }}
          onClose={() => setIsMonthPickerOpen(false)}
        >
          <S.YearsDropdownContainer>
            {filteredYears.map((year) => (
              <S.Year
                key={year.year}
                $active={year.active}
                type="button"
                {...yearButton(year)}
                onClick={(e) => {
                  yearButton(year).onClick?.(e)
                  setIsMonthPickerOpen(false)
                }}
              >
                {year.year}
              </S.Year>
            ))}
          </S.YearsDropdownContainer>
        </Popper>
        {showNextMonthButton && (
          <S.MonthHeaderNextArrowContainer>
            <IconButton
              icon={ChevronRightIcon}
              size="small"
              aria-label="Move forward to switch to the next month."
              {...addOffset({ months: 1 })}
            />
          </S.MonthHeaderNextArrowContainer>
        )}
      </S.MonthHeader>
      <S.MonthDaysContainer>
        {weekDays.map((weekDay) => (
          <S.WeekDay key={weekDay}>{weekDay}</S.WeekDay>
        ))}
        {days.map((day) => (
          <S.Day
            type="button"
            key={day.$date.toDateString()}
            $hidden={!day.inCurrentMonth}
            $selected={day.selected}
            $inRange={day.range === 'in-range' || day.range === 'will-be-in-range'}
            {...dayButton(day)}
            disabled={dayButton(day).disabled || !day.inCurrentMonth}
          >
            {day.inCurrentMonth ? day.day : null}
          </S.Day>
        ))}
      </S.MonthDaysContainer>
    </S.MonthContainer>
  )
}
